import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Spin, Alert } from 'antd';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LabelList,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';
import {
  getDashboardMetric15,
  type DashboardMetric15CycleInterimTestingBreakdown,
} from '@/services/dashboardService';
import { CompletedStatus } from '@/pages/exhibits/ex5/ControlsFilterDrawer';

const { Text } = Typography;

// Colors per requirement
const COLORS = {
  notStarted: '#D1D5DB', // grey
  wip: '#FACC15', // yellow
  completed: '#22C55E', // green
};

type Row = {
  cycleId: string;
  cycleLabel: string; // "A00 Revenue (N=45)"
  total: number;

  // raw counts
  notStartedCount: number;
  wipCount: number;
  completedCount: number;

  // percentages (0..100)
  notStartedPct: number;
  wipPct: number;
  completedPct: number;
};

const pct = (value: number) => `${Math.round(value)}%`;

const safePercent = (part: number, total: number) => {
  if (!total || total <= 0) return 0;
  return (part / total) * 100;
};

const Metric15Tooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;

  const row: Row | undefined = payload?.[0]?.payload;
  if (!row) return null;

  // payload contains one entry per Bar series for the hovered stack
  const byKey: Record<string, any> = {};
  payload.forEach((p: any) => {
    if (p?.dataKey) byKey[p.dataKey] = p;
  });

  const ns = byKey['notStartedPct']?.value ?? row.notStartedPct;
  const wip = byKey['wipPct']?.value ?? row.wipPct;
  const comp = byKey['completedPct']?.value ?? row.completedPct;

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 8,
        padding: 10,
        minWidth: 240,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{row.cycleLabel}</div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          rowGap: 6,
          columnGap: 12,
        }}
      >
        <span>Not Started</span>
        <span style={{ fontWeight: 600 }}>
          {pct(ns)} ({row.notStartedCount})
        </span>

        <span>WIP</span>
        <span style={{ fontWeight: 600 }}>
          {pct(wip)} ({row.wipCount})
        </span>

        <span>Completed</span>
        <span style={{ fontWeight: 600 }}>
          {pct(comp)} ({row.completedCount})
        </span>
      </div>

      <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.6)' }}>
        Click a segment to open Exhibit 5
      </div>
    </div>
  );
};

export type EX1InterimTestingCompletionStatusByCycleStackedChartProps = {
  height?: number;
  title?: string;
};

export const EX1InterimTestingCompletionStatusByCycleStackedChart: React.FC<
  EX1InterimTestingCompletionStatusByCycleStackedChartProps
> = ({ height = 380, title = 'Interim Testing completion status' }) => {
  const navigate = useNavigate();

  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const [raw, setRaw] = useState<
    DashboardMetric15CycleInterimTestingBreakdown[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setRaw([]);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric15(annualReportId);
        if (!isMounted) return;
        setRaw(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!isMounted) return;
        setRaw([]);
        setErrorMessage(
          err?.message ||
            err?.Message ||
            'Failed to load Metric 15 (Interim testing completion breakdown by cycle).',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  const data: Row[] = useMemo(() => {
    return (
      raw
        .map(x => {
          const total = x.controlCount ?? 0;

          const notStartedCount = x.notStartedCount ?? 0;
          const wipCount = x.wipCount ?? 0;
          const completedCount = x.completedCount ?? 0;

          // Prefer controlCount as denominator; if backend ever sends 0 but counts exist, fallback
          const denom =
            total > 0 ? total : notStartedCount + wipCount + completedCount;

          return {
            cycleId: x.cycleId,
            cycleLabel: `${x.cycleCode} ${x.cycleName} (Count: ${denom})`,
            total: denom,

            notStartedCount,
            wipCount,
            completedCount,

            notStartedPct: safePercent(notStartedCount, denom),
            wipPct: safePercent(wipCount, denom),
            completedPct: safePercent(completedCount, denom),
          };
        })
    );
  }, [raw]);

  const isEmpty = !loading && !errorMessage && data.length === 0;

  // Click behavior:
  // segment click -> Exhibit 5 filtered by cycle + interim status (and active + in-scope implied)
  const goToExhibit5 = (cycleId: string, cycleLabel: string, segment: CompletedStatus) => {
    const cycleCode = cycleLabel.split(' ')[0];
    const cycleName = cycleLabel.split(' ')[1];
    navigate('/control-activities', {
      state: { 
        cycleId: cycleId,
        cycleCode: cycleCode ?? '',
        cycleName: cycleName ?? '',
        interimStatus: segment,
        testingScope: true
       },
    });
  };

  // Labels inside each segment show percent (hide tiny slices)
  const PercentLabel = (props: any) => {
    const { x, y, width, height, value } = props;
    const v = Number(value ?? 0);
    if (!Number.isFinite(v) || v < 8) return null; // hide very small segments
    const cx = x + width / 2;
    const cy = y + height / 2;
    return (
      <text
        x={cx}
        y={cy}
        textAnchor='middle'
        dominantBaseline='middle'
        fontSize={12}
        fontWeight={600}
      >
        {pct(v)}
      </text>
    );
  };

  // Right axis: total counts
  const RightCountTick = (props: any): React.ReactElement => {
    const { x, y, payload } = props;
    const row: Row | undefined = payload?.payload;
    if (!row) return <text />;
    return (
      <text
        x={x}
        y={y}
        dy={4}
        textAnchor='start'
        fontSize={12}
        fill='rgba(0,0,0,0.65)'
      >
        n = {row.total}
      </text>
    );
  };

  // Legend labels
  const legendFormatter = (value: any) => {
    if (value === 'notStartedPct') return 'Not Started';
    if (value === 'wipPct') return 'WIP';
    if (value === 'completedPct') return 'Completed';
    return value;
  };

  return (
    <Card title={title}>
      <div style={{ height }}>
        {loading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin />
          </div>
        ) : errorMessage ? (
          <Alert
            type='error'
            message='Dashboard Metric 15 Error'
            description={errorMessage}
            showIcon
          />
        ) : isEmpty ? (
          <Alert
            type='info'
            message='No data'
            description='No active in-scope controls were returned for interim testing completion.'
            showIcon
          />
        ) : (
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart
              data={data}
              layout='vertical'
              barCategoryGap={10}
              margin={
                isNarrow
                  ? { top: 8, right: 10, bottom: 40, left: 10 }
                  : { top: 8, right: 60, bottom: 8, left: 10 }
              }
            >
              <Tooltip content={<Metric15Tooltip />} />

              {/* Legend: right on wide screens, bottom on narrow (same fix pattern as metric 2) */}
              <Legend
                layout={isNarrow ? 'horizontal' : 'vertical'}
                align={isNarrow ? 'center' : 'right'}
                verticalAlign={isNarrow ? 'bottom' : 'middle'}
                wrapperStyle={
                  isNarrow ? { paddingTop: 12 } : { paddingLeft: 16 }
                }
                formatter={legendFormatter}
              />

              {/* X Axis: percentage */}
              <XAxis
                type='number'
                domain={[0, 100]}
                tickFormatter={v => `${v}%`}
                allowDecimals={false}
              />

              {/* Y Axis: Cycle label with N */}
              <YAxis
                type='category'
                dataKey='cycleLabel'
                width={isNarrow ? 110 : 190}
                tick={{ fontSize: 12 }}
              />

              {/* Right Axis: total count "n = 20" */}
              <YAxis
                yAxisId='right'
                orientation='right'
                type='category'
                dataKey='cycleLabel'
                axisLine={false}
                tickLine={false}
                width={isNarrow ? 40 : 55}
                tick={RightCountTick}
              />

              {/* Stacks: 100% */}
              <Bar
                dataKey='notStartedPct'
                stackId='a'
                fill={COLORS.notStarted}
                isAnimationActive={false}
                onClick={(_, idx) => {
                  const row = data[idx];
                  if (row?.cycleId) goToExhibit5(row.cycleId, row.cycleLabel, 'NotStarted');
                }}
                style={{ cursor: 'pointer' }}
              >
                <LabelList dataKey='notStartedPct' content={PercentLabel} />
              </Bar>

              <Bar
                dataKey='wipPct'
                stackId='a'
                fill={COLORS.wip}
                isAnimationActive={false}
                onClick={(_, idx) => {
                  const row = data[idx];
                  if (row?.cycleId) goToExhibit5(row.cycleId, row.cycleLabel, 'WIP');
                }}
                style={{ cursor: 'pointer' }}
              >
                <LabelList dataKey='wipPct' content={PercentLabel} />
              </Bar>

              <Bar
                dataKey='completedPct'
                stackId='a'
                fill={COLORS.completed}
                isAnimationActive={false}
                onClick={(_, idx) => {
                  const row = data[idx];
                  if (row?.cycleId) goToExhibit5(row.cycleId, row.cycleLabel, 'Completed');
                }}
                style={{ cursor: 'pointer' }}
              >
                <LabelList dataKey='completedPct' content={PercentLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {!loading && !errorMessage ? (
        <div style={{ marginTop: 8, textAlign: 'center' }}>
          <Text type='secondary'>
            Active, in-scope controls grouped by cycle.
          </Text>
        </div>
      ) : null}
    </Card>
  );
};

export default EX1InterimTestingCompletionStatusByCycleStackedChart;
